import { useSupabaseQuerySingle } from '@/hooks/use-supabase-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth/auth-context'
import { mapRow, toDbRow } from '@/lib/db-utils'
import type { Profile } from '@/types/profile'

interface UseProfileReturn {
  profile: Profile | null
  loading: boolean
  error: string | null
  updateProfile: (data: Partial<Profile>) => Promise<void>
  refetch: () => Promise<void>
}

export function useProfile(): UseProfileReturn {
  const { user } = useAuth()

  const {
    data: profile,
    loading,
    error,
    refetch,
  } = useSupabaseQuerySingle<Profile>({
    queryFn: (client) => client.from('profiles').select('*').eq('id', user!.id).single(),
    mapper: (data) => mapRow<Profile>(data as Record<string, unknown>),
  })

  const updateProfile = async (data: Partial<Profile>) => {
    if (!user) return

    const { error: err } = await supabase
      .from('profiles')
      .update(toDbRow(data as Record<string, unknown>))
      .eq('id', user.id)

    if (err) throw new Error(err.message)

    // Auto-match cherished number when GRID number is set or changed.
    // If the user's GRID number matches a pre-grandfathered cherished number
    // that has no holder_id yet, claim it for this user.
    if (data.gridNumber) {
      const numericMatch = data.gridNumber.match(/\d+/)
      if (numericMatch) {
        const gridNum = parseInt(numericMatch[0], 10)
        await supabase
          .from('cherished_number_reservations')
          .update({ holder_id: user.id, updated_at: new Date().toISOString() })
          .eq('grid_number', gridNum)
          .eq('status', 'GRANDFATHERED')
          .is('holder_id', null)
      }
    }

    await refetch()
  }

  return { profile, loading, error, updateProfile, refetch }
}
